import React, { useRef, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Logo from 'assets/images/Logo blue.png'
import { Loader } from "semantic-ui-react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { SG_GET_CHAT_GPT_SUMMARY } from "constants/actions";
import { v4 as uuidv4 } from "uuid";
import { structurePrompt } from './structurePrompt'
import { words } from './translations'

const NewValidationToken = uuidv4();

// Simulation styles
const simulationOptions = {
  thoughtful: "Provide a thoughtful, reflective and detailed response, with personal insights",
  disengaged: "Provide a minimal, uninterested response showing little engagement",
  negative: "Provide a critical, unsatisfied response highlighting frustrations",
  positive: "Provide an enthusiastic, appreciative response with positive feedback"
};


const Conversation = ({ responses, setScreen, response, id, saveResponse, language, tone = 'balanced' }) => {
  const dispatch = useDispatch();
  const timeout = useRef(null);
  const [loading, setLoading] = useState(true);
  const textareaRef = useRef();
  const [question, setQuestion] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [requestType, setRequestType] = useState("question");

  const { get_chat_gpt } = useSelector(
    (state) => ({
      get_chat_gpt: state.chatgpt,
    }),
    shallowEqual
  );

  // Get styling based on selected tone
  const getToneStyle = () => {
    return { 
      color: '#2D70E2', 
      subtitle: language === 'EN' ? 'Feedback Survey' : 
                language === 'FR' ? 'Enquête de feedback' : 
                'Encuesta de retroalimentación' 
    };
  };
  
  const { color, subtitle } = getToneStyle();

  // Initial load
  useEffect(() => {
    console.log("Initial load effect triggered");
    setLoading(true);
    setRequestType("question");
    fetchQuestion();
    
    // Debug message
    console.log("Loading state initialized to true");
  }, [id]);

  // Monitor loading state and responses
  useEffect(() => {
    console.log("Response monitoring effect triggered", {
      loading,
      pending: get_chat_gpt?.response_pending,
      hasResponse: !!get_chat_gpt?.chat_gpt_summary?.response,
      requestType
    });

    // Handle timeout for loading state
    if (get_chat_gpt?.response_pending === true) {
      console.log("Setting timeout for loading state");
      timeout.current = setTimeout(() => {
        setLoading(false);
      }, 15000);
    }

    // When response is received
    if (get_chat_gpt?.response_pending === false) {
      console.log("Response received, clearing timeout");
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      
      if (get_chat_gpt?.chat_gpt_summary?.response) {
        console.log("Processing response for request type:", requestType);
        
        if (requestType === "question") {
          // Update question
          console.log("Updating question:", get_chat_gpt.chat_gpt_summary.response);
          setQuestion(get_chat_gpt.chat_gpt_summary.response);
          setLoading(false);
          console.log("Loading state set to false after question update");
        } 
        else if (requestType === "simulation") {
          // Update simulation response
          console.log("Updating simulation response:", get_chat_gpt.chat_gpt_summary.response);
          const simulatedResponse = get_chat_gpt.chat_gpt_summary.response;
          setFeedbackText(simulatedResponse);
          
          // Save the response
          const ResponseObj = {
            response: simulatedResponse,
            question: question,
            type: 'ai'
          };
          saveResponse(ResponseObj);
          setSimulationLoading(false);
        }
      } else {
        // Response received but no content
        console.log("Response received but no content");
        setLoading(false);
        if (requestType === "simulation") {
          setSimulationLoading(false);
        }
      }
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [get_chat_gpt, requestType]);
 
  const handleChange = (event) => {
    setFeedbackText(event.target.value);
    const ResponseObj = {
      response: event.target.value,
      question: question,
      type: 'ai'
    };
    saveResponse(ResponseObj);
  };

  function fetchQuestion() {
    console.log("Fetching question");
    let _prompt = structurePrompt(responses, language, tone);

    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: {
        payload: _prompt,
        token: NewValidationToken
      },
    });
  }

  const save = () => {
    setQuestion("");
    setFeedbackText("");
    setScreen();
  }

  const simulateResponse = (style) => {
    console.log("Simulating response for style:", style);
    setSimulationLoading(true);
    setRequestType("simulation");
    
    // Use ChatGPT for simulation
    console.log("Using ChatGPT for simulation");
    const simulationPrompt = `
      Question: ${question}
      Instruction: ${simulationOptions[style]}
      Make sure this is in response to an organizational survey.
      Generate a response (20-50 words):
    `;

    const simulationToken = uuidv4();
    
    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: {
        payload: simulationPrompt,
        token: simulationToken
      },
    });
  }

  // Determine if content after question should be shown
  const shouldShowContent = !loading && question;

  // Force the loading state to true when in development - for debugging
  // const loadingState = true; // Uncomment to always show skeleton

  return (
    <MainContainer>
      <FeedbackContainer key={id}>
        <TitleContainer>
          <Title style={{ color }}>
            <IMG src={Logo} />
          </Title>
          <Subtitle>{subtitle}</Subtitle>
        </TitleContainer>

        
        <ContentSection>
          <QuestionSection>
            {!question ? (
              <SkeletonContainer>
                <SkeletonLine />
                <SkeletonLine width="92%" />
                <SkeletonLine width="96%" />
                <SkeletonLine width="88%" />
                <SkeletonLine width="76%" />
              </SkeletonContainer>
            ) : (
              <Description>{question}</Description>
            )}
          </QuestionSection>
          
          {shouldShowContent && (
            <>
              <StyledTextarea
                value={feedbackText}
                onChange={handleChange}
                placeholder={words['textarea'][language]}
                ref={textareaRef}
                style={{ borderColor: response ? color : '#ced4da' }}
              />

              <SimulationContainer>
                <SimulationTitle>Simulate response</SimulationTitle>
                <SimulationDescription>Select a style to automatically generate a response</SimulationDescription>
                <SimulationOptions>
                  {simulationLoading ? (
                    <Loader active inline size="small" />
                  ) : (
                    <>
                      <SimulationBubble onClick={() => simulateResponse('thoughtful')}>Thoughtful</SimulationBubble>
                      <SimulationBubble onClick={() => simulateResponse('disengaged')}>Disengaged</SimulationBubble>
                      <SimulationBubble onClick={() => simulateResponse('negative')}>Negative</SimulationBubble>
                      <SimulationBubble onClick={() => simulateResponse('positive')}>Positive</SimulationBubble>
                    </>
                  )}
                </SimulationOptions>
              </SimulationContainer>

              <ButtonArea>
                <Button 
                  onClick={() => save()} 
                  disabled={response == undefined}
                  style={{ backgroundColor: color }}
                >
                  {words['Next'][language]}
                </Button>
              </ButtonArea>
            </>
          )}
        </ContentSection>
      </FeedbackContainer>
    </MainContainer>
  )
}

export default Conversation

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Shimmer animation for skeleton loader
const shimmerAnimation = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Main container to fix vertical positioning
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  margin-top: 40px;
`;

// Simulation styled components
const SimulationContainer = styled.div`
  width: 100%;
  margin-top: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  animation: ${fadeIn} 0.5s ease-out forwards;
`;

const SimulationTitle = styled.h4`
  font-family: "Raleway", sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #2a3039;
`;

const SimulationDescription = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 12px 0;
`;

const SimulationOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
`;

const SimulationBubble = styled.div`
  flex: 1;
  min-width: 100px;
  padding: 10px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  text-align: center;
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2D70E2;
    background-color: #f0f7ff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  width: 100%;
  font-family: "Raleway", sans-serif;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const QuestionSection = styled.div`
  width: 100%;
  min-height: 80px; /* Ensure consistent height for skeleton and question */
  margin-bottom: 34px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  width: 100%;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6B7280;
  margin-top: 5px;
`;

const Description = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.6;
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const Title = styled.div`
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  display: flex;
`;

const IMG = styled.img`
  width: 120px;
`;

const StyledTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #2a3039;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: none;
  font-family: "Raleway", sans-serif;
  height: 150px;
  animation: ${fadeIn} 0.5s ease-out forwards;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #2d70e2;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(45, 112, 226, 0.25);
  }
`;

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  color: white;
  font-size: 16px;
  font-weight: 400;
  border: none;
  border-radius: 5px;
  padding: 10px 35px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 40px;
  
  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: #ccc !important;
    cursor: not-allowed;
  }
`;

// Improved Skeleton Loader components
const SkeletonContainer = styled.div`
  width: 100%;
  display: block;
`;

const SkeletonLine = styled.div`
  display: block;
  height: 16px;
  margin-bottom: 12px;
  width: ${props => props.width || '100%'};
  background: #f6f7f8;
  background: linear-gradient(to right, #eee 2%, #ddd 18%, #eee 33%);
  background-size: 800px 100%;
  animation: ${shimmerAnimation} 1.2s infinite linear;
  border-radius: 2px;
`;